import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import ButtonPublish from '../../../../components/_button/ButtonPublish';
import DialogConfirm from '../../../../components/_dialog/DialogConfirm';
import { useDeleteShippingMethodsMutation } from '../../../../../graphql/mutation/shippingMethod/deleteShippingMethodsMutation';
import { useCreateGetsShippingMethodMutation } from '../../../../../graphql/mutation/shippingMethod/createGetsShippingMethodMutation';
import ButtonDelete from '../../../../components/_button/ButtonDelete';
import BusinessShippingMethodTableItem from './BusinessShippingMethodTableItem';
import {
  IShippingMethodQueryData,
  IShippingMethodVars,
  useShippingMethodQuery,
} from '../../../../../graphql/query/shippingMethod/useShippingMethodQuery';
import useNotification from '../../../../components/_hook/useNotification';
import useRouter from '../../../../components/_hook/useRouter';
import ButtonUnpublish from '../../../../components/_button/ButtonUnpublish';
import GraphQLTable from '../../../../components/_table/GraphQLTable';
import { useUnpublishShippingMethodsMutation } from '../../../../../graphql/mutation/shippingMethod/unpublishShippingMethodsMutation';
import ButtonCreate from '../../../../components/_button/ButtonCreate';
import { usePublishShippingMethodsMutation } from '../../../../../graphql/mutation/shippingMethod/publishShippingMethodsMutation';
import useRoutes from '../../../../components/_hook/useRoutes';
import { IShippingMethodSchema } from '../../../../../graphql/type/shippingMethod';
import BusinessLayout from '../../../../layout/Business';
import Context from '../../../../context';
import Helmet from '../../../../components/Helmet';
import { ITranslationSubTypeInput } from '../../../../../graphql/type/_inputTypes/translation';
import { useCompanyQuery } from '../../../../../graphql/query/company/useCompanyQuery';

function BusinessShippingMethodTable() {
  interface IGetsShipping {
    name: ITranslationSubTypeInput[];
    description: ITranslationSubTypeInput[];
    status: boolean;
    shippingMethodType: string;
    company: string;

    getsShippingConfigUserId: string;
    getsShippingConfigEmailId: string;
    getsShippingConfigPhoneId: string;
    getsShippingConfigAddressId: string;
  }

  const { t } = useTranslation();
  const { notification } = useNotification();
  const { history } = useRouter();
  const { company } = useContext(Context);
  const { path } = useRoutes();
  const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);
  const [selectedItems, setSelectedItems] = useState<IShippingMethodSchema[]>(
    []
  );
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [hasGetsShippingMethod, setHasGetsShippingMethod] = useState(false);
  const { data: getsShippingMethod, loading: getsShippingMethodLoading } = useShippingMethodQuery(
    `
      _id
      status
      shipping_method
    `,
    { variables: {
      filter: {
        shippingMethod: 'gets_shipping' as string,
        companyId : company._id as string
      },
      limit: 1,
    },
  });
  const [deleteShippingMethodsMutation] = useDeleteShippingMethodsMutation(
    `
    isSuccess
  `,
    {
      onCompleted: () => {
        notification.success(
          t('__item___deleted', {
            item: `${selectedItems.length} ${t('shipping_method', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
        setTableRefreshKey(new Date().getTime());
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [publishShippingMethodsMutation] = usePublishShippingMethodsMutation(
    `updatedShippingMethods {
				_id
				status
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('shipping_method', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    unpublishShippingMethodsMutation,
  ] = useUnpublishShippingMethodsMutation(
    `updatedShippingMethods {
				_id
				status
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('shipping_method', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  const [ createGetsShippingMethodMutation,
        { loading: isCreatingGetsShippingMethod },
    ] = useCreateGetsShippingMethodMutation(
        `createdGetsShippingMethod {
    			_id
    		}`,
        {
            onCompleted: (tempData) => {
                notification.success(
                    t('__item___added', { item: t('gets_shipping')})
                );
                if(tempData && tempData.createGetsShippingMethod
                    && tempData.createGetsShippingMethod.createdGetsShippingMethod && tempData.createGetsShippingMethod.createdGetsShippingMethod._id){
                        history.push(
                            path('business.shipping_method.detail', {
                                companyID: company._id,
                                docID: tempData.createGetsShippingMethod.createdGetsShippingMethod._id,
                            })
                        )
                }
            },
            onError: (e) => {
                notification.error(e.message);
            },
        }
    );

  useEffect(() => {
    const [tempGetsShippingMethod] = getsShippingMethod && getsShippingMethod.shippingMethods && getsShippingMethod.shippingMethods.items
      ? getsShippingMethod.shippingMethods.items
      : [];

    if(tempGetsShippingMethod) {
      setHasGetsShippingMethod(true)
    }

  },[getsShippingMethodLoading])

  function toggleDeleteDialog() {
    setIsOpenDeleteDialog(!isOpenDeleteDialog);
  }

  async function onClickPublish() {
    await publishShippingMethodsMutation({
      variables: { _ids: selectedItems.map((item) => item._id) },
    });
  }

  async function onClickUnpublish() {
    await unpublishShippingMethodsMutation({
      variables: { _ids: selectedItems.map((item) => item._id) },
    });
  }

  async function onClickDelete() {
    await deleteShippingMethodsMutation({
      variables: { _ids: selectedItems.map((item) => item._id) },
    });
  }

  async function onClickGetsShipping() {
      const defaultValues: IGetsShipping = {
        name: [{la : 'en', text : 'Global Shipping'}],
        description: [{ la : 'en', text : '' }],
        status: true,
        shippingMethodType: 'gets_shipping',
        company: company._id,

        getsShippingConfigUserId: '',
        getsShippingConfigEmailId: '',
        getsShippingConfigPhoneId: '',
        getsShippingConfigAddressId: '',
      };

      await createGetsShippingMethodMutation({ variables: defaultValues });

  }


  return (
    <>
      <Helmet title={`${t('shipping_method')} | ${t('business')}`} />
      <DialogConfirm
        title={t('are_you_sure_you_want_to_delete')}
        open={isOpenDeleteDialog}
        onClose={toggleDeleteDialog}
        onConfirm={onClickDelete}
      />
      <Grid container justifyContent="space-between">
        <ButtonCreate
            variant="contained"
            color="primary"
            text={t('new')}
            onClick={() =>
                history.push(
                path('business.shipping_method.detail', {
                companyID: company._id,
                docID: 'new',
                })
                )
            }
        />
        { !hasGetsShippingMethod &&
            <Button
                color="primary"
                variant="contained"
                onClick={() => {
                    onClickGetsShipping();
                }}
            >
                {t('use_gets_shipping')}
            </Button>
        }
      </Grid>
      <GraphQLTable<
        IShippingMethodQueryData,
        IShippingMethodSchema,
        IShippingMethodVars
      >
        refreshKey={tableRefreshKey}
        select={`
				_id
				company
				name {
					la
					text
				}
				status
				updated {
					time
				}
				created {
					time
				}
       	 `}
        useQuery={useShippingMethodQuery}
        renderHeader={[
          {
            field: '_id',
            label: t('id'),
            align: 'left',
            sortable: true,
          },
          {
            field: 'name',
            label: t('name'),
            align: 'center',
          },
          {
            field: 'createdTime',
            label: t('created'),
            align: 'center',
            sortable: true,
          },
          {
            field: 'updatedTime',
            label: t('updated'),
            align: 'center',
            sortable: true,
          },
          {
            field: 'action',
            label: t('action'),
            align: 'right',
          },
        ]}
        renderItem={(item) => (
          <BusinessShippingMethodTableItem
            item={item}
            setTableRefreshKey={setTableRefreshKey}
          />
        )}
        useCheckbox
        onItemSelected={setSelectedItems}
        defaultFilterOptions={{
          companyId: company._id,
        }}
        selectedItems={selectedItems}
        searchFields={{ _id: { label: t('id') } }}
        title={t('shipping_method', { count: 2 })}
        defaultSort="createdTime"
        defaultSortOrder={-1}
        actionButtons={[
          <ButtonPublish
            key="publish"
            variant="contained"
            color="primary"
            size="small"
            text={t('publish')}
            onClick={onClickPublish}
          />,
          <ButtonUnpublish
            key="unpublish"
            color="primary"
            variant="contained"
            size="small"
            text={t('unpublish')}
            onClick={onClickUnpublish}
          />,
          <ButtonDelete
            key="delete"
            variant="contained"
            size="small"
            text={t('delete')}
            onClick={toggleDeleteDialog}
          />,
        ]}
        itemKey="_id"
      />
    </>
  );
}

export default () => <BusinessLayout main={<BusinessShippingMethodTable />} />;
